import { useEffect, useState } from 'react';
import type { WebViewerInstance } from '@pdftron/webviewer';
import { Hand, TextSelect } from 'lucide-react';
import { useI18n } from '../../i18n';
import { Button } from './ui/button';

interface PdfSelectionToolbarProps {
  instance: WebViewerInstance;
  disabled: boolean;
}

/** Explicit reader tools: Apryse's mobile default is Pan, and its standard
 * selection menu selects annotations rather than activating TextSelect. */
export function PdfSelectionToolbar({ instance, disabled }: PdfSelectionToolbarProps) {
  const { t } = useI18n();
  const { documentViewer, Tools } = instance.Core;
  const [activeTool, setActiveTool] = useState(() => documentViewer.getToolMode().name);

  useEffect(() => {
    const updateTool = () => setActiveTool(documentViewer.getToolMode().name);
    updateTool();
    documentViewer.addEventListener('toolModeUpdated', updateTool);
    return () => documentViewer.removeEventListener('toolModeUpdated', updateTool);
  }, [documentViewer]);

  // Apryse 11.9 declares ToolNames as a numeric enum, but its runtime values
  // and Tool.name are strings. Normalize at that SDK boundary.
  const selecting = activeTool === String(Tools.ToolNames.TEXT_SELECT);
  const panning = activeTool === String(Tools.ToolNames.PAN);

  return (
    <div className="shrink-0 border-b border-[#7367FF]/15 bg-white px-2 py-1 sm:px-4">
      <div
        role="group"
        aria-label={t('reader.selection.tools')}
        className="flex flex-wrap items-center gap-2 md:justify-end"
      >
        <Button
          type="button"
          variant={selecting ? 'default' : 'outline'}
          aria-pressed={selecting}
          disabled={disabled}
          onClick={() => instance.UI.setToolMode(Tools.ToolNames.TEXT_SELECT)}
          className={`min-h-11 px-3 ${selecting ? 'bg-[#6358e8] text-white hover:bg-[#554acb]' : 'border-[#7367FF]/40 text-[#5148cc]'}`}
        >
          <TextSelect aria-hidden="true" className="h-4 w-4" />
          {t('reader.selection.selectText')}
        </Button>
        <Button
          type="button"
          variant={panning ? 'secondary' : 'ghost'}
          aria-pressed={panning}
          disabled={disabled}
          onClick={() => instance.UI.setToolMode(Tools.ToolNames.PAN)}
          className="min-h-11 px-3 text-[#434343]"
        >
          <Hand aria-hidden="true" className="h-4 w-4" />
          {t('reader.selection.movePage')}
        </Button>
        {selecting && !disabled && (
          <p role="status" className="basis-full text-xs leading-relaxed text-gray-600 sm:basis-auto [@media(pointer:fine)]:hidden">
            {t('reader.selection.touchHint')}
          </p>
        )}
      </div>
    </div>
  );
}
